/**
 * Mounts once at the app root and keeps the dashboard fresh without polling.
 *
 * The root cause of "you have to manually refresh" is mechanical: `main.tsx` sets `staleTime: 30_000`
 * **plus** `refetchOnWindowFocus: false`, so a mounted query whose user does nothing never refetches —
 * ever. The 30 s only bounds unmount→remount. This restores focus-driven freshness, but NARROWLY: one
 * content-free cursor read says which domains moved, and only those query keys are invalidated. Blanket
 * focus-refetching is what `main.tsx` deliberately turned off (it re-mints every presigned preview URL),
 * so staying narrow is the entire point.
 *
 * ## Signals
 *
 * - **`focusManager.subscribe`** — TanStack's own normalised focus/visibility signal, so this fires on
 *   exactly the events its focus-refetching would have. Covers "staffer comes back to the tab".
 * - **A 10-minute heartbeat while visible** — covers the case focus never catches: a wall-mounted iPad
 *   that is visible all day and never loses focus. This is the plan's correctness floor, not a poll.
 *
 * Cost: one ~200-byte request per signal, throttled. Nothing when the tab is hidden or signed out.
 */
import { focusManager, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/lib/api/hooks';
import { getIdToken, useAuth } from '@/lib/auth-state';
import {
  allRefetchableQueryKeys,
  changedDomains,
  type DomainRevisions,
  invalidationPlan,
} from '@/lib/change-feed';
import { type ChangePushTarget, createChangeSocket } from '@/lib/change-socket';
import { connectChangeChannel } from '@/lib/change-socket-amplify';
import { markQueryStale } from '@/lib/stale-signal';

/** Floor between cursor reads, so alt-tab drumming cannot turn into a request storm. */
const MIN_CHECK_INTERVAL_MS = 5_000;
/** The visible-tab heartbeat — the plan's ~10-minute staleness floor. */
const HEARTBEAT_MS = 10 * 60_000;
/**
 * How long to wait before rebuilding a socket that exhausted its retry budget (Phase 7d).
 *
 * 15 minutes, matching the socket's own ~12-minute healthy recycle, so a re-arm looks like an ordinary
 * reconnect rather than a new traffic pattern. The arithmetic that matters is the failure case: a broken
 * socket costs 5 attempts per re-arm, i.e. ~20/hour/client — a trickle that self-heals within 15 minutes of
 * the cause clearing, and nothing like the hammering R8 exists to prevent. Each of those bursts is also
 * what keeps the 7d alarm latched (see the M-of-N reasoning in `monitoring-stack.ts`), so a still-broken
 * socket keeps announcing itself instead of going quiet after the first give-up.
 */
const SOCKET_REARM_MS = 15 * 60_000;

/**
 * Field-wise equality for the advertised socket config, so an unchanged config keeps its object identity
 * and the socket effect does not re-run. Exported for its test — the failure it prevents is silent: a
 * healthy connection torn down and rebuilt on every heartbeat, which reads as "the socket works" while
 * quietly dropping every event in each gap.
 */
export function samePushTarget(
  a: ChangePushTarget | undefined,
  b: ChangePushTarget | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.endpoint === b.endpoint && a.region === b.region && a.channel === b.channel;
}

export function ChangeFeedWatcher(): null {
  const queryClient = useQueryClient();
  const client = useApiClient();
  const { state } = useAuth();
  const signedIn = state.status === 'signed-in';

  const lastSeen = useRef<DomainRevisions | undefined>(undefined);
  const wasDisabled = useRef(false);
  const inFlight = useRef(false);
  const lastCheckedAt = useRef(0);
  const [pushTarget, setPushTarget] = useState<ChangePushTarget | undefined>(undefined);
  /** Bumped to rebuild a socket that gave up — see `onGaveUp` below. */
  const [socketEpoch, setSocketEpoch] = useState(0);
  const rearmTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * One cursor read. ⚠️ The 5-second spacing lives in {@link requestCheck}, NOT here — an early return
   * inside this function silently DISCARDS the caller's request, and one of its callers is the
   * reconnect resync, which is a correctness operation that must not be dropped.
   */
  const runCheck = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const { data } = await client.GET('/changes');
      const body = data?.ok ? data.data : undefined;
      // Where to subscribe. Absent ⇒ the server says there is no socket, so we never connect — which is
      // how Phase 7c ships inert while `CHANGE_PUBLISH_ENABLED` is false. Identity is preserved when the
      // config is unchanged so the heartbeat cannot recycle a healthy connection every 10 minutes.
      const nextPush = body?.feedEnabled ? body.push : undefined;
      setPushTarget((prev) => (samePushTarget(prev, nextPush) ? prev : nextPush));

      // `domains` is absent exactly when the feed is untrustworthy (reader or publisher switched off).
      // Do NOT fall back to invalidating everything: that is `refetchOnWindowFocus: true`, the behaviour
      // main.tsx removed on purpose. Better to leave screens as they are than reintroduce a known
      // performance regression — the cursor is an optimisation, and without it we simply do nothing.
      if (!body?.feedEnabled || !body.domains) {
        wasDisabled.current = true;
        return;
      }

      const next = body.domains as DomainRevisions;
      const prev = lastSeen.current;
      lastSeen.current = next;

      // Coming back from a disabled feed: while the publisher was off, writes happened but were never
      // counted, so the cursor UNDERSTATES reality and a diff would report nothing. One full refresh of
      // the refetchable keys is the only honest recovery.
      if (wasDisabled.current) {
        wasDisabled.current = false;
        if (prev) {
          for (const key of allRefetchableQueryKeys()) {
            queryClient.invalidateQueries({ queryKey: [key] });
          }
        }
        return;
      }

      // First successful read establishes the baseline. Invalidating here would refetch everything on
      // every page load, when the queries had just been fetched anyway.
      if (!prev) return;

      const changed = changedDomains(prev, next);
      if (changed.length === 0) return;

      const { refetch, markStale } = invalidationPlan(changed);
      for (const key of refetch) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
      for (const key of markStale) {
        // Marked invalid but NOT refetched — see MARK_STALE_ONLY. Refreshing the press worklist under a
        // staffer's finger can move a tap onto the wrong garment and fire settleLine against it.
        queryClient.invalidateQueries({ queryKey: [key], refetchType: 'none' });
        // Offer the refresh instead of applying it. This store — not the query's own `isInvalidated` — is
        // the signal, because the press page invalidates the SAME key in its own mutation `onSettled`, so
        // the cache flag cannot tell a change on another station from the operator's own tap.
        markQueryStale(key);
      }
    } catch {
      // A network blip or a 401 during token refresh is not worth surfacing; the next signal retries.
      // Deliberately swallowed: this is a freshness aid, never a correctness dependency.
    } finally {
      inFlight.current = false;
      lastCheckedAt.current = Date.now();
      // A request that arrived while this one was in flight is honoured now rather than lost. Without this
      // chain, a ring landing mid-read would be silently dropped and the screen would sit stale until the
      // heartbeat — which is exactly the symptom this phase exists to remove.
      if (pending.current) {
        pending.current = false;
        requestCheckRef.current?.();
      }
    }
  }, [client, queryClient]);

  /**
   * Trailing-edge throttle: the ONLY entry point for a freshness signal (socket ring, reconnect resync,
   * focus, heartbeat). Guarantees every request eventually runs while collapsing a burst into one read.
   *
   * ⚠️ Needed BECAUSE of the socket, and the earlier shape was wrong twice over. `runCheck` used to hold
   * the 5-second floor itself and simply RETURN when inside it. For focus events that was harmless — a
   * dropped one just means the tab was already fresh. For events it is the bug: three writes on the floor
   * in two seconds ring three times, the last two vanish, and the screen sits stale for ten minutes. And
   * the reconnect resync — the correctness floor for everything published while we were disconnected —
   * was dropped outright whenever a reconnect happened within 5 s of a read, which a recycle makes likely.
   *
   * So: spacing is enforced HERE by deferring, never by discarding, and an in-flight read re-runs on
   * completion (see `runCheck`'s `finally`).
   */
  const pending = useRef(false);
  const pendingTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const requestCheckRef = useRef<(() => void) | undefined>(undefined);

  const requestCheck = useCallback(() => {
    if (inFlight.current) {
      pending.current = true; // picked up by runCheck's finally
      return;
    }
    const sinceLast = Date.now() - lastCheckedAt.current;
    if (sinceLast >= MIN_CHECK_INTERVAL_MS) {
      void runCheck();
      return;
    }
    if (pendingTimer.current !== undefined) return; // already deferred — a burst collapses to one read
    pendingTimer.current = setTimeout(() => {
      pendingTimer.current = undefined;
      // Re-enter rather than calling runCheck directly, so state that changed while we waited (another
      // read having just landed) is re-evaluated instead of assumed.
      requestCheckRef.current?.();
    }, MIN_CHECK_INTERVAL_MS - sinceLast);
  }, [runCheck]);

  requestCheckRef.current = requestCheck;

  useEffect(
    () => () => {
      if (pendingTimer.current !== undefined) clearTimeout(pendingTimer.current);
    },
    [],
  );

  useEffect(() => {
    if (!signedIn) return;

    requestCheck();
    const unsubscribe = focusManager.subscribe((visible) => {
      if (visible) requestCheck();
    });
    const heartbeat = setInterval(() => {
      // Hidden tabs are skipped so a backgrounded iPad costs nothing. Kept even with the socket live: it
      // is the plan's correctness floor for a dropped broadcast or a half-open connection, not a poll.
      if (document.visibilityState === 'visible') requestCheck();
    }, HEARTBEAT_MS);

    return () => {
      unsubscribe();
      clearInterval(heartbeat);
    };
  }, [signedIn, requestCheck]);

  /**
   * The push socket (Phase 7c). Opened only once a cursor read has told us where to connect, so the very
   * first `GET /changes` doubles as the enable check — no build-time flag, and nothing to redeploy to turn
   * the socket off. `signedIn` gates it because the connection is authenticated with the ID token.
   *
   * Re-runs only when the TARGET ITSELF changes: `check` keeps the previous object when the fields match
   * (see `samePushTarget`), so the 10-minute heartbeat re-reading the same config does not tear down and
   * rebuild a healthy connection every time.
   *
   * `socketEpoch` is a deliberate re-run TRIGGER, not a value the body reads — bumping it is how a
   * given-up socket gets rebuilt (see `onGaveUp`). Biome's rule models dependencies as inputs and cannot
   * express that, so the suppression is the honest way to say "this dep exists to force the effect".
   * Removing it does not simplify anything: it silently disables the re-arm.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: socketEpoch forces the re-arm; see above.
  useEffect(() => {
    if (!signedIn || !pushTarget) return;
    const socket = createChangeSocket({
      target: pushTarget,
      getIdToken,
      onRing: requestCheck,
      // Events published while we were disconnected are gone, so a fresh connection must reconcile against
      // the durable cursor. Routed through the throttle DELIBERATELY: it defers rather than discards, so
      // the resync still happens (a few seconds later at worst) instead of being dropped when a reconnect
      // lands inside the window — which a 12-minute recycle right after a read makes routine.
      onConnected: requestCheck,
      // ⚠️ The socket stops itself after 5 consecutive failures (plan R8: a kiosk whose refresh token died
      // must not become a permanent connect storm). Correct as a BURST guard, wrong as a permanent verdict —
      // a shop-WiFi outage at 6am would otherwise leave every tablet un-live for the rest of the day, and
      // nothing on screen would say so. Re-arming here keeps the burst cap where it belongs (inside the
      // socket: mechanism) and the give-up-forever policy out of it (here: policy), so 7c's tested
      // behaviour is unchanged — a NEW socket is built rather than the dead one revived.
      // No de-duplication guard: `giveUp()` sets `stopped` and every path bails on it, so ONE socket gives
      // up at most once — and the effect cleanup stops the old socket before building a new one, so two can
      // never overlap. A guard here would defend an unreachable state, and its test could not fail.
      onGaveUp: () => {
        rearmTimer.current = setTimeout(() => {
          rearmTimer.current = undefined;
          setSocketEpoch((n) => n + 1);
        }, SOCKET_REARM_MS);
      },
      connect: connectChangeChannel,
    });
    socket.start();
    return () => socket.stop();
    // `socketEpoch` is the re-arm trigger: bumping it tears this socket down and builds a fresh one.
  }, [signedIn, pushTarget, requestCheck, socketEpoch]);

  /**
   * Release a pending re-arm on unmount. HYGIENE ONLY, and deliberately untested: what actually stops a
   * fired timer from rebuilding a socket for a dead component is React making `setSocketEpoch` a no-op
   * after unmount, so a test asserting "no socket after unmount" passes with this removed. Verified by
   * mutation. Kept because leaving a 15-minute timer alive is still wrong; NOT counted as covered.
   */
  useEffect(
    () => () => {
      if (rearmTimer.current !== undefined) clearTimeout(rearmTimer.current);
    },
    [],
  );

  return null;
}
